import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Edit, Trash2, Eye, EyeOff, Tag, Code, FileText, MessageSquare, Brain, Sparkles, Copy, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

interface AIPrompt {
  id: string;
  name: string;
  description?: string;
  prompt_type: string;
  template_category?: string;
  content: string;
  version: string;
  is_active: boolean;
  is_default: boolean;
  tags: string[];
  metadata: any;
  created_at: string;
  updated_at: string;
}

interface FormData {
  name: string;
  description: string;
  prompt_type: string;
  template_category: string;
  content: string;
  version: string;
  is_active: boolean;
  is_default: boolean;
  tags: string[];
  metadata: any;
}

const AIPromptsManagement: React.FC = () => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<AIPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const promptTypes = [
    { value: 'analysis', label: '分析类', icon: Brain, color: 'blue' },
    { value: 'chat', label: '聊天类', icon: MessageSquare, color: 'green' },
    { value: 'summary', label: '摘要类', icon: FileText, color: 'purple' },
    { value: 'evaluation', label: '评估类', icon: Sparkles, color: 'orange' }
  ];

  const templateCategories = [
    'deep_insight',
    'news_summary', 
    'tutor',
    'review',
    'recommendation',
    'custom'
  ];

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=prompts&action=list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setPrompts(result.data.data || []);
      } else {
        console.error('获取提示词失败:', result.error);
        toast.error('获取提示词失败');
      }
    } catch (error) {
      console.error('获取提示词失败:', error);
      toast.error('获取提示词失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`确定要删除提示词「${name}」吗？`)) return;

    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=prompts&action=delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success('提示词删除成功');
      await fetchPrompts();
    } catch (error) {
      console.error('删除提示词失败:', error);
      toast.error('删除失败：' + (error as Error).message);
    }
  };

  const handleEdit = (prompt: AIPrompt) => {
    navigate(`/admin/prompts/edit/${prompt.id}`);
  };

  const handleCreate = () => {
    navigate('/admin/prompts/new');
  };

  const handleCopyContent = async (content: string, id: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(id);
      toast.success('内容已复制到剪贴板');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('复制失败');
    }
  };


  const filteredPrompts = prompts.filter(prompt => {
    const matchesSearch = prompt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (prompt.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || prompt.prompt_type === typeFilter;
    const matchesActive = activeFilter === 'all' || 
                         (activeFilter === 'active' && prompt.is_active) ||
                         (activeFilter === 'inactive' && !prompt.is_active);
    return matchesSearch && matchesType && matchesActive;
  });

  const getTypeInfo = (type: string) => {
    return promptTypes.find(t => t.value === type) || promptTypes[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Brain className="w-6 h-6 mr-2 text-blue-600" />
              提示词管理
            </h2>
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              添加提示词
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* 搜索和筛选 */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索提示词名称或描述..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部类型</option>
              {promptTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">全部状态</option>
              <option value="active">已启用</option>
              <option value="inactive">已禁用</option>
            </select>
          </div>

          {/* 提示词列表 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredPrompts.map((prompt) => {
              const typeInfo = getTypeInfo(prompt.prompt_type);
              const TypeIcon = typeInfo.icon;
              
              return (
                <div key={prompt.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg bg-${typeInfo.color}-100`}>
                        <TypeIcon className={`w-5 h-5 text-${typeInfo.color}-600`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{prompt.name}</h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${typeInfo.color}-100 text-${typeInfo.color}-800`}>
                            {typeInfo.label}
                          </span>
                          {prompt.is_default && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              默认
                            </span>
                          )}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            prompt.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {prompt.is_active ? (
                              <><Eye className="w-3 h-3 mr-1" />启用</>
                            ) : (
                              <><EyeOff className="w-3 h-3 mr-1" />禁用</>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleCopyContent(prompt.content, prompt.id)}
                        className="text-gray-400 hover:text-gray-600"
                        title="复制内容"
                      >
                        {copiedId === prompt.id ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(prompt)}
                        className="text-blue-600 hover:text-blue-900"
                        title="编辑"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(prompt.id, prompt.name)}
                        className="text-red-600 hover:text-red-900"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {prompt.description && (
                    <p className="text-gray-600 text-sm mb-3">{prompt.description}</p>
                  )}

                  <div className="bg-gray-50 rounded-md p-3 mb-4">
                    <div className="text-xs text-gray-500 mb-1">提示词内容预览</div>
                    <div className="text-sm text-gray-700 line-clamp-3">
                      {prompt.content.substring(0, 200)}
                      {prompt.content.length > 200 && '...'}
                    </div>
                  </div>

                  {prompt.tags && prompt.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {prompt.tags.map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                          <Tag className="w-3 h-3 mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>版本: {prompt.version}</span>
                    <span>更新: {new Date(prompt.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredPrompts.length === 0 && (
            <div className="text-center py-12">
              <Brain className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">暂无提示词数据</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default AIPromptsManagement;